import { setPolygons } from "@/features/survey/surveySlice";
import { updateCurrentSurvey } from "@survey/surveySlice";
import type { MapPolygon } from "@survey-types/survey.polygon.types";
import { DEFAULT_POLYGON_STYLE } from "@survey-types/survey.polygon.types";
import { surveyApi } from "./base";

const parseCoordinates = (value: unknown): number[][][] => {
  if (Array.isArray(value)) {
    return value as number[][][];
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed as number[][][];
      }
    } catch {
      // Ignore and fall through to default
    }
  }

  return [];
};

const normalizePolygon = (
  raw: Record<string, unknown>,
  surveyId: string,
  index: number,
): MapPolygon => {
  const geometry = raw.geometry as Record<string, unknown> | undefined;
  const rawCoordinates =
    raw.coordinates ?? geometry?.coordinates ?? raw.polygonCoordinates;
  const coordinates = parseCoordinates(rawCoordinates);

  const rawStyle = (raw.style as Record<string, unknown> | undefined) ?? {};

  return {
    polygonId:
      (raw.polygonId as string) ||
      (raw.id as string) ||
      (raw.polygon_id as string) ||
      `polygon-${index}`,
    surveyId:
      (raw.surveyId as string) ||
      (raw.survey_id as string) ||
      surveyId,
    name: (raw.name as string) || (raw.label as string) || `Polygon ${index + 1}`,
    showLabel:
      typeof raw.showLabel === "boolean"
        ? raw.showLabel
        : typeof raw.isLabelVisible === "boolean"
          ? (raw.isLabelVisible as boolean)
          : true,
    showBorder:
      typeof raw.showBorder === "boolean"
        ? raw.showBorder
        : undefined,
    coordinates,
    style: {
      fillColor:
        (rawStyle.fillColor as string) ||
        (raw.fillColor as string) ||
        DEFAULT_POLYGON_STYLE.fillColor,
      fillOpacity:
        Number(rawStyle.fillOpacity ?? raw.fillOpacity) ||
        DEFAULT_POLYGON_STYLE.fillOpacity,
      strokeColor:
        (rawStyle.strokeColor as string) ||
        (raw.borderColor as string) ||
        (raw.strokeColor as string) ||
        DEFAULT_POLYGON_STYLE.strokeColor,
      strokeWidth:
        Number(rawStyle.strokeWidth ?? raw.borderWidth ?? raw.strokeWidth) ||
        DEFAULT_POLYGON_STYLE.strokeWidth,
      strokeOpacity:
        Number(rawStyle.strokeOpacity ?? raw.borderOpacity ?? raw.strokeOpacity) ||
        DEFAULT_POLYGON_STYLE.strokeOpacity,
    },
    isHidden:
      typeof raw.isHidden === "boolean"
        ? raw.isHidden
        : typeof raw.isDeleted === "boolean"
          ? (raw.isDeleted as boolean)
          : false,
    order: Number(raw.order ?? index),
    createdOn: raw.createdOn as string | undefined,
    updatedOn: raw.updatedOn as string | undefined,
    createdBy: raw.createdBy as string | undefined,
    updatedBy: raw.updatedBy as string | undefined,
  };
};

const normalizePolygonList = (
  payload: unknown,
  surveyId: string,
): MapPolygon[] => {
  if (Array.isArray(payload)) {
    return payload.map((item, index) =>
      normalizePolygon((item as Record<string, unknown>) || {}, surveyId, index),
    );
  }

  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    const nestedList =
      obj.polygons ?? obj.items ?? obj.data ?? obj.result ?? obj.records;

    if (Array.isArray(nestedList)) {
      return nestedList.map((item, index) =>
        normalizePolygon((item as Record<string, unknown>) || {}, surveyId, index),
      );
    }
  }

  return [];
};

/**
 * Polygon API - handles survey-scoped polygon CRUD.
 * Endpoints:
 * GET    /polygons/{survey_id}
 * POST   /polygons/{survey_id}
 * PATCH  /polygons/{survey_id}/{polygon_id}
 * DELETE /polygons/{survey_id}/{polygon_id}
 */
export const surveyPolygonsApi = surveyApi
  .enhanceEndpoints({ addTagTypes: ["Polygons"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getPolygons: build.query<MapPolygon[], string>({
        query: (surveyId) => ({
          url: `/polygons/${surveyId}`,
          method: "GET",
        }),
        providesTags: (_result, _error, surveyId) => [
          { type: "Polygons", id: surveyId },
        ],
        async onQueryStarted(surveyId, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const polygons = normalizePolygonList(data, surveyId);
            dispatch(setPolygons(polygons));

            // Keep persisted polygons visible after refresh even when API does not
            // return isIncludePolygons in survey payload.
            if (polygons.length > 0) {
              dispatch(updateCurrentSurvey({ id: surveyId, isIncludePolygons: true }));
            }
          } catch {
            dispatch(setPolygons([]));
          }
        },
      }),

      createPolygon: build.mutation<
        MapPolygon,
        { surveyId: string; body: Partial<MapPolygon> }
      >({
        query: ({ surveyId, body }) => ({
          url: `/polygons/${surveyId}`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Polygons", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Caller handles rollback
          }
        },
      }),

      updatePolygon: build.mutation<
        MapPolygon,
        {
          surveyId: string;
          polygonId: string;
          body: Partial<MapPolygon>;
        }
      >({
        query: ({ surveyId, polygonId, body }) => ({
          url: `/polygons/${surveyId}/${polygonId}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Polygons", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Caller handles rollback
          }
        },
      }),

      deletePolygon: build.mutation<
        void,
        {
          surveyId: string;
          polygonId: string;
        }
      >({
        query: ({ surveyId, polygonId }) => ({
          url: `/polygons/${surveyId}/${polygonId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Polygons", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Caller handles rollback
          }
        },
      }),
    }),
  });

export const {
  useGetPolygonsQuery,
  useCreatePolygonMutation,
  useUpdatePolygonMutation,
  useDeletePolygonMutation,
} = surveyPolygonsApi;